/**
 * PHARMAKON — Fournisseur ERP · shared constants (Module 00)
 *
 * Single source of truth for the supplier-side role vocabulary, status → tone
 * maps (ERP V3 Badge tones — NO hardcoded hex), and notification metadata.
 * Replaces the monolith's hex-based portal.config.js maps (DESIGN_DIRECTIVE §3).
 *
 * Tone values map to the Badge primitive: neutral|accent|success|warning|danger|info.
 */

// ── Supplier roles (Implementation_Plan §3.2.1) ──────────────────────────────
// The 7 granular roles + the two legacy roles (treated as ADMIN-equivalent).
export const SUPPLIER_ROLES = Object.freeze([
  "FOURNISSEUR_ADMIN",
  "FOURNISSEUR_RH",
  "FOURNISSEUR_MANAGER",
  "FOURNISSEUR_COMPTABLE",
  "FOURNISSEUR_LOGISTIQUE",
  "FOURNISSEUR_COMMERCIAL",
  "FOURNISSEUR_ANALYSTE",
  // Legacy — kept for compatibility during the migration window.
  "FOURNISSEUR",
  "LABORATOIRE",
]);

export const isSupplierRole = (role) => SUPPLIER_ROLES.includes(role);

/** Roles treated as the supplier organisation administrator. */
export const SUPPLIER_ADMIN_ROLES = Object.freeze([
  "FOURNISSEUR_ADMIN",
  "FOURNISSEUR",
  "LABORATOIRE",
]);

export const SUPPLIER_ROLE_LABELS = Object.freeze({
  FOURNISSEUR_ADMIN: "Administrateur",
  FOURNISSEUR_RH: "Ressources humaines",
  FOURNISSEUR_MANAGER: "Manager",
  FOURNISSEUR_COMPTABLE: "Comptable",
  FOURNISSEUR_LOGISTIQUE: "Logistique",
  FOURNISSEUR_COMMERCIAL: "Commercial",
  FOURNISSEUR_ANALYSTE: "Analyste",
  FOURNISSEUR: "Fournisseur",
  LABORATOIRE: "Laboratoire",
});

export const supplierRoleLabel = (role) =>
  SUPPLIER_ROLE_LABELS[role] || role || "—";

/**
 * Client-side RBAC mirror (server is authoritative). Maps a granular UI
 * capability → the supplier roles allowed. Used only to show/hide affordances;
 * the API re-checks every action. Legacy roles inherit ADMIN capabilities.
 * Source: Implementation_Plan §8 / Appendix F (supplier rows).
 */
const ADMIN = SUPPLIER_ADMIN_ROLES;
export const SUPPLIER_CAPABILITIES = Object.freeze({
  "efacture.create": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_COMMERCIAL"],
  "efacture.send": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_COMMERCIAL"],
  "efacture.record_payment": [...ADMIN, "FOURNISSEUR_COMPTABLE"],
  "efacture.modify_respond": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_COMMERCIAL"],
  "bl.create": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_LOGISTIQUE"],
  "fournisseur_staff.manage": [...ADMIN, "FOURNISSEUR_RH"],
  // ── Module 02 — Catalogue, connexions & PPA (server constants.js:463) ───────
  // These were MISSING from this mirror while CatalogueList / ConnexionsList /
  // PpaReferenceManager all gate their write half on `fournisseur_catalogue.
  // manage`. `supplierCan` returns false for an unknown capability, so the
  // entire write half of Module 02 was invisible for EVERY supplier role even
  // though the server authorises SUP_COMMERCIAL. Mirrors the server matrix:
  //   fournisseur_catalogue { read: SUP_ALL, manage: SUP_COMMERCIAL }
  //   fournisseur_pricing   { manage: SUP_COMMERCIAL }
  "fournisseur_catalogue.read": [...SUPPLIER_ROLES],
  "fournisseur_catalogue.manage": [
    ...ADMIN,
    "FOURNISSEUR_MANAGER",
    "FOURNISSEUR_COMMERCIAL",
  ],
  "fournisseur_pricing.manage": [
    ...ADMIN,
    "FOURNISSEUR_MANAGER",
    "FOURNISSEUR_COMMERCIAL",
  ],
  // ── Module 01 — Upstream Procurement (Implementation_Plan §8 / Appendix F) ──
  // read = SUP_ALL (incl. ANALYSTE, read-only); manage = manufacturers + PO
  // authoring; receive = goods-in. Server matrix is authoritative.
  "fournisseur_procurement.read": [...SUPPLIER_ROLES],
  "fournisseur_procurement.manage": [...ADMIN, "FOURNISSEUR_MANAGER"],
  "fournisseur_procurement.receive": [
    ...ADMIN,
    "FOURNISSEUR_MANAGER",
    "FOURNISSEUR_LOGISTIQUE",
  ],
  // All supplier roles can read the feed / dashboard.
  "fournisseur_notification.read": [...SUPPLIER_ROLES],
  // ── Module 03 — Commercial (Implementation_Plan §8 / Appendix F) ────────────
  // read = SUP_ALL (pré-commandes inbox, offres, BC, promotions list); manage =
  // SUP_COMMERCIAL (offer building/sending, negotiation, BC confirm/cancel,
  // promotion CRUD). The pharmacy side uses pharmacy auth (not modelled here).
  "fournisseur_commercial.read": [...SUPPLIER_ROLES],
  "fournisseur_commercial.manage": [
    ...ADMIN,
    "FOURNISSEUR_MANAGER",
    "FOURNISSEUR_COMMERCIAL",
  ],
  // ── Module 04 — Warehouse & Lots (Implementation_Plan §8 / Appendix F) ───────
  // read = SUP_ALL; manage = entrepôt/emplacement/stock/transfer mutations.
  "fournisseur_warehouse.read": [...SUPPLIER_ROLES],
  "fournisseur_warehouse.manage": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_LOGISTIQUE"],
  // ── Module 05 — Logistics & Delivery (Implementation_Plan §8 / Appendix F) ───
  // The shared BL document: create/mark = SUP_LOGISTIQUE; read = both sides.
  // bl.create is declared above (Module 00). The reserved fournisseur_colis is
  // extended with read/manage/scan/track; fleet rides fournisseur_colis.manage.
  // Server matrix (assertFournisseurScope + pharmacie_id) stays authoritative.
  "fournisseur_colis.read": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_LOGISTIQUE", "FOURNISSEUR_ANALYSTE"],
  "fournisseur_colis.manage": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_LOGISTIQUE"],
  "fournisseur_colis.scan": [...ADMIN, "FOURNISSEUR_MANAGER", "FOURNISSEUR_LOGISTIQUE"],
  "fournisseur_colis.track": [...SUPPLIER_ROLES],
  // ── Module 06 — Finance & Settlement (Implementation_Plan §8 / Appendix F) ───
  // FIN-1: REUSE the reserved fournisseur_compta + fournisseur_tresorerie
  // resources. read = SUP_ALL (any supplier staff incl. read-only ANALYSTE);
  // manage / close = SUP_COMPTABLE (the accountant — record payments, create
  // avoirs, set credit limits, close a period). The server matrix
  // (assertFournisseurScope + the COMPTABILITE_PRO gate) stays authoritative.
  "fournisseur_compta.read": [...SUPPLIER_ROLES],
  "fournisseur_compta.manage": [...ADMIN, "FOURNISSEUR_COMPTABLE"],
  "fournisseur_compta.close": [...ADMIN, "FOURNISSEUR_COMPTABLE"],
  "fournisseur_tresorerie.read": [...SUPPLIER_ROLES],
  "fournisseur_tresorerie.manage": [...ADMIN, "FOURNISSEUR_COMPTABLE"],
  // ── Module 08 — Subscription & Monetization (Implementation_Plan §8 / SUB-8) ─
  // REUSE the reserved fournisseur_subscription resource. read = SUP_ALL (any
  // supplier staff consults the org's subscription / entitlements / history);
  // manage = the billing owner (FOURNISSEUR_ADMIN + legacy) — subscribe /
  // upgrade / downgrade / cancel / reactivate / buy add-ons / start the trial.
  "fournisseur_subscription.read": [...SUPPLIER_ROLES],
  "fournisseur_subscription.manage": [...ADMIN],
});

/** True when `role` may perform `capability` (client-side hint only). */
export const supplierCan = (role, capability) => {
  const allowed = SUPPLIER_CAPABILITIES[capability];
  return allowed ? allowed.includes(role) : false;
};

// ── E-Facture status → { label, tone, icon } (lucide icon names) ─────────────
export const EFACTURE_STATUS_META = Object.freeze({
  CREEE: { label: "Brouillon", tone: "neutral", icon: "file-pen" },
  ENVOYEE: { label: "Envoyée", tone: "info", icon: "send" },
  NOTIFIEE: { label: "Notifiée", tone: "info", icon: "bell" },
  IMPORTEE: { label: "Importée", tone: "accent", icon: "download" },
  DEMANDE_MODIFICATION: {
    label: "Modification demandée",
    tone: "warning",
    icon: "pencil",
  },
  VALIDEE: { label: "Validée", tone: "success", icon: "check-circle" },
  PARTIELLEMENT_ACCEPTEE: {
    label: "Partiellement acceptée",
    tone: "warning",
    icon: "circle-dashed",
  },
  REJETEE: { label: "Rejetée", tone: "danger", icon: "x-circle" },
  PAYEE: { label: "Payée", tone: "success", icon: "banknote" },
  ANNULEE: { label: "Annulée", tone: "neutral", icon: "ban" },
});

export const efactureStatusMeta = (statut) =>
  EFACTURE_STATUS_META[statut] || {
    label: statut || "—",
    tone: "neutral",
    icon: "circle",
  };

// ── BL status → tone ─────────────────────────────────────────────────────────
// Module 05 grows StatutBL additively with the transit lifecycle (PREPARE →
// CHARGE → EN_TRANSIT, + INCIDENT). The legacy 4 values are preserved unchanged.
export const BL_STATUS_META = Object.freeze({
  EN_ATTENTE: { label: "En attente", tone: "warning", icon: "clock" },
  PREPARE: { label: "Préparé", tone: "info", icon: "package-check" },
  CHARGE: { label: "Chargé", tone: "info", icon: "package-plus" },
  EN_TRANSIT: { label: "En transit", tone: "accent", icon: "truck" },
  PARTIELLEMENT_LIVRE: { label: "Partiel", tone: "warning", icon: "package" },
  LIVRE: { label: "Livré", tone: "success", icon: "check-circle" },
  INCIDENT: { label: "Incident", tone: "danger", icon: "alert-triangle" },
  REFUSE: { label: "Refusé", tone: "danger", icon: "x-circle" },
});

export const blStatusMeta = (statut) =>
  BL_STATUS_META[statut] || { label: statut || "—", tone: "neutral", icon: "package" };

// ── Modification thread metadata ─────────────────────────────────────────────
export const MODIF_STATUS_META = Object.freeze({
  PROPOSEE: { label: "En attente", tone: "warning", icon: "hourglass" },
  ACCEPTEE: { label: "Acceptée", tone: "success", icon: "check" },
  REFUSEE: { label: "Refusée", tone: "danger", icon: "x" },
  EXPIREE: { label: "Expirée", tone: "neutral", icon: "clock" },
});

export const modifStatusMeta = (statut) =>
  MODIF_STATUS_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

export const MODIF_ACTOR_LABELS = Object.freeze({
  PHARMACIE: "Pharmacie",
  FOURNISSEUR: "Vous (fournisseur)",
});

export const MODIFICATION_MAX_TURNS = 3;

// ── Structured rejection motives (Implementation_Plan §3.3.3) ────────────────
export const MOTIF_REJET_OPTIONS = Object.freeze([
  { value: "PRIX", label: "Prix incorrect" },
  { value: "QUANTITE", label: "Quantité incorrecte" },
  { value: "PRODUIT_NON_COMMANDE", label: "Produit non commandé" },
  { value: "QUALITE", label: "Problème de qualité" },
  { value: "DELAI", label: "Délai non respecté" },
  { value: "DOUBLON", label: "Facture en doublon" },
  { value: "AUTRE", label: "Autre" },
]);

// ── Notification type → { label, tone, icon } ────────────────────────────────
export const NOTIF_TYPE_META = Object.freeze({
  EFACTURE_RECUE: { label: "E-facture reçue", tone: "info", icon: "file-text" },
  FACTURE_IMPORTEE: { label: "Facture importée", tone: "accent", icon: "download" },
  EFACTURE_ACCEPTEE: { label: "Facture acceptée", tone: "success", icon: "check-circle" },
  BR_CREE: { label: "Bon de réception", tone: "accent", icon: "package-check" },
  BL_VALIDE: { label: "Bon de livraison validé", tone: "success", icon: "truck" },
  FACTURE_REJETEE: { label: "Facture rejetée", tone: "danger", icon: "x-circle" },
  MODIFICATION_DEMANDEE: { label: "Modification demandée", tone: "warning", icon: "pencil" },
  MODIFICATION_REPONDUE: { label: "Réponse modification", tone: "info", icon: "message-square" },
  FACTURE_PAYEE: { label: "Paiement reçu", tone: "success", icon: "banknote" },
  PRECOMMANDE_URGENTE: { label: "Pré-commande urgente", tone: "danger", icon: "zap" },
  PHARMACIE_ACCEPTED: { label: "Pharmacie a accepté", tone: "success", icon: "check-circle" },
  PHARMACIE_REJECTED: { label: "Pharmacie a rejeté", tone: "danger", icon: "x-circle" },
  // Legacy values still emitted by the current backend.
  FACTURE_VALIDEE: { label: "Facture validée", tone: "success", icon: "check-circle" },
  DEMANDE_MODIFICATION: { label: "Modification demandée", tone: "warning", icon: "pencil" },
  PAIEMENT_RECU: { label: "Paiement reçu", tone: "success", icon: "banknote" },
  NOUVELLE_PRECOMMANDE: { label: "Pré-commande", tone: "info", icon: "shopping-cart" },
  MESSAGE_PHARMACIE: { label: "Message", tone: "info", icon: "message-square" },
  COMPTE_VALIDE: { label: "Compte validé", tone: "success", icon: "user-check" },
  // Refonte WP-34 (D-20, ux/03 §5.9) — réclamations received from pharmacies
  RECLAMATION_RECUE: { label: "Nouvelle réclamation", tone: "warning", icon: "message-square-warning" },
  RECLAMATION_MESSAGE: { label: "Réclamation — message", tone: "info", icon: "message-square" },
  RECLAMATION_CLOTUREE: { label: "Réclamation clôturée", tone: "neutral", icon: "check-circle" },
  SYSTEM: { label: "Système", tone: "neutral", icon: "settings" },
});

export const notifTypeMeta = (type) =>
  NOTIF_TYPE_META[type] || { label: type || "Notification", tone: "neutral", icon: "bell" };

/** Notification priority → Badge tone. */
export const NOTIF_PRIORITY_TONE = Object.freeze({
  LOW: "neutral",
  NORMAL: "info",
  HIGH: "danger",
});

// ════════════════════════════════════════════════════════════════════════════
// MODULE 01 — UPSTREAM PROCUREMENT
// ════════════════════════════════════════════════════════════════════════════

// ── Manufacturier type → { label, tone, icon } (Laboratoire.type) ─────────────
export const MANUFACTURIER_TYPE_META = Object.freeze({
  FABRICANT: { label: "Fabricant", tone: "accent", icon: "factory" },
  IMPORTATEUR: { label: "Importateur", tone: "info", icon: "ship" },
});

export const manufacturierTypeMeta = (type) =>
  MANUFACTURIER_TYPE_META[type] || { label: type || "—", tone: "neutral", icon: "building-2" };

export const MANUFACTURIER_TYPE_OPTIONS = Object.freeze([
  { value: "FABRICANT", label: "Fabricant" },
  { value: "IMPORTATEUR", label: "Importateur" },
]);

// ── Purchase-order status → { label, tone, icon } (PurchaseOrderStatut, §7) ───
export const PO_STATUS_META = Object.freeze({
  BROUILLON: { label: "Brouillon", tone: "neutral", icon: "file-pen" },
  ENVOYE: { label: "Envoyé", tone: "info", icon: "send" },
  CONFIRME: { label: "Confirmé", tone: "accent", icon: "check-circle" },
  PARTIELLEMENT_RECU: {
    label: "Partiellement reçu",
    tone: "warning",
    icon: "package",
  },
  RECU: { label: "Reçu", tone: "success", icon: "package-check" },
  CLOTURE: { label: "Clôturé", tone: "success", icon: "archive" },
  ANNULE: { label: "Annulé", tone: "danger", icon: "ban" },
});

export const poStatusMeta = (statut) =>
  PO_STATUS_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

/** Status filter tabs for the PO list (in document-flow order). */
export const PO_STATUS_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "BROUILLON", label: "Brouillons" },
  { value: "ENVOYE", label: "Envoyés" },
  { value: "CONFIRME", label: "Confirmés" },
  { value: "PARTIELLEMENT_RECU", label: "Partiels" },
  { value: "RECU", label: "Reçus" },
  { value: "CLOTURE", label: "Clôturés" },
  { value: "ANNULE", label: "Annulés" },
]);

/** States from which a goods-in receipt may be recorded (BR-PROC mirror). */
export const PO_RECEIVABLE_STATES = Object.freeze([
  "CONFIRME",
  "PARTIELLEMENT_RECU",
]);

/** States from which a draft PO may still be edited / its lines changed. */
export const PO_EDITABLE_STATES = Object.freeze(["BROUILLON"]);

/** States from which a PO may be cancelled (pre-received). */
export const PO_CANCELLABLE_STATES = Object.freeze([
  "BROUILLON",
  "ENVOYE",
  "CONFIRME",
]);

// ── Currencies for import POs (M2 — FX). DZD is the settlement currency. ──────
export const DEVISE_OPTIONS = Object.freeze([
  { value: "DZD", label: "DZD — Dinar algérien" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "USD", label: "USD — Dollar US" },
  { value: "GBP", label: "GBP — Livre sterling" },
  { value: "CHF", label: "CHF — Franc suisse" },
]);

/** Controlled-substance register movement type → { label, tone, icon }. */
export const PSYCHOTROPE_MOUVEMENT_META = Object.freeze({
  ACQUISITION: { label: "Acquisition", tone: "info", icon: "arrow-down-to-line" },
  CESSION: { label: "Cession", tone: "warning", icon: "arrow-up-from-line" },
  AJUSTEMENT: { label: "Ajustement", tone: "neutral", icon: "sliders-horizontal" },
});

export const psychotropeMouvementMeta = (type) =>
  PSYCHOTROPE_MOUVEMENT_META[type] ||
  { label: type || "—", tone: "neutral", icon: "circle" };

// ════════════════════════════════════════════════════════════════════════════
// MODULE 02 — CATALOGUE & PRICING
// ════════════════════════════════════════════════════════════════════════════

// ── Catalogue entry status → { label, tone, icon } (CatalogueStatut, §7.1) ────
export const CATALOGUE_STATUT_META = Object.freeze({
  BROUILLON: { label: "Brouillon", tone: "neutral", icon: "file-pen" },
  ACTIF: { label: "Actif", tone: "success", icon: "check-circle" },
  INACTIF: { label: "Inactif", tone: "warning", icon: "pause" },
  ARCHIVE: { label: "Archivé", tone: "neutral", icon: "archive" },
});

export const catalogueStatutMeta = (statut) =>
  CATALOGUE_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

/** Status filter tabs for the catalogue list (lifecycle order). */
export const CATALOGUE_STATUT_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "ACTIF", label: "Actifs" },
  { value: "BROUILLON", label: "Brouillons" },
  { value: "INACTIF", label: "Inactifs" },
  { value: "ARCHIVE", label: "Archivés" },
]);

/** Statuts a catalogue entry may be set to from the lifecycle action menu. */
export const CATALOGUE_STATUT_OPTIONS = Object.freeze([
  { value: "BROUILLON", label: "Brouillon" },
  { value: "ACTIF", label: "Actif" },
  { value: "INACTIF", label: "Inactif" },
  { value: "ARCHIVE", label: "Archivé" },
]);

// ── Visibilité → { label, tone, icon, hint } (CatalogueVisibilite) ────────────
export const CATALOGUE_VISIBILITE_META = Object.freeze({
  GLOBAL: {
    label: "Public",
    tone: "success",
    icon: "globe",
    hint: "Visible par toutes les pharmacies connectées.",
  },
  PAR_PHARMACIE: {
    label: "Pharmacies sélectionnées",
    tone: "info",
    icon: "users",
    hint: "Visible uniquement par les pharmacies que vous choisissez.",
  },
  PRIVE: {
    label: "Privé",
    tone: "neutral",
    icon: "lock",
    hint: "Jamais visible (sur arrangement direct uniquement).",
  },
});

export const catalogueVisibiliteMeta = (v) =>
  CATALOGUE_VISIBILITE_META[v] || { label: v || "—", tone: "neutral", icon: "eye-off", hint: "" };

export const CATALOGUE_VISIBILITE_OPTIONS = Object.freeze([
  { value: "GLOBAL", label: "Public (toutes pharmacies connectées)" },
  { value: "PAR_PHARMACIE", label: "Pharmacies sélectionnées" },
  { value: "PRIVE", label: "Privé (sur arrangement)" },
]);

// ── Disponibilité → { label, tone, icon } (DisponibiliteCatalogue, CAT-12) ────
export const CATALOGUE_DISPO_META = Object.freeze({
  DISPONIBLE: { label: "Disponible", tone: "success", icon: "check-circle" },
  RUPTURE: { label: "En rupture", tone: "danger", icon: "x-circle" },
  SUR_COMMANDE: { label: "Sur commande", tone: "warning", icon: "clock" },
});

export const catalogueDispoMeta = (d) =>
  CATALOGUE_DISPO_META[d] || { label: d || "—", tone: "neutral", icon: "circle" };

export const CATALOGUE_DISPO_OPTIONS = Object.freeze([
  { value: "DISPONIBLE", label: "Disponible" },
  { value: "RUPTURE", label: "En rupture" },
  { value: "SUR_COMMANDE", label: "Sur commande" },
]);

// ── Unité de vente → label (UniteVente) ───────────────────────────────────────
export const UNITE_VENTE_LABELS = Object.freeze({
  BOITE: "Boîte",
  FLACON: "Flacon",
  UNITE: "Unité",
  AUTRE: "Autre",
});

export const uniteVenteLabel = (u) => UNITE_VENTE_LABELS[u] || u || "—";

export const UNITE_VENTE_OPTIONS = Object.freeze([
  { value: "BOITE", label: "Boîte" },
  { value: "FLACON", label: "Flacon" },
  { value: "UNITE", label: "Unité" },
  { value: "AUTRE", label: "Autre" },
]);

// ── Shared Annexe classification (CAT-5/GC2 — never a parallel enum) ──────────
export const CLASSIFICATION_LABELS = Object.freeze({
  LISTE_I: "Liste I",
  LISTE_II: "Liste II",
  HORS_LISTE: "Hors liste",
  TABLEAU_B: "Tableau B",
});

export const classificationLabel = (c) => CLASSIFICATION_LABELS[c] || c || "—";

export const CLASSIFICATION_OPTIONS = Object.freeze([
  { value: "LISTE_I", label: "Liste I" },
  { value: "LISTE_II", label: "Liste II" },
  { value: "HORS_LISTE", label: "Hors liste" },
  { value: "TABLEAU_B", label: "Tableau B" },
]);

/** Map a nomenclature `liste` string → the shared ListeClassification enum (CAT-7). */
export const listeToClassification = (liste) => {
  if (!liste) return "";
  const s = String(liste).trim().toUpperCase();
  if (s.includes("TABLEAU") && s.includes("B")) return "TABLEAU_B";
  if (s.includes("II") || s.includes("2")) return "LISTE_II";
  if (s.includes("I") || s.includes("1")) return "LISTE_I";
  if (s.includes("HORS")) return "HORS_LISTE";
  return "";
};

// ── Source discriminator (SourceCatalogue, OD-4) ──────────────────────────────
export const SOURCE_LABELS = Object.freeze({
  ACHETE: "Acheté",
  FABRIQUE: "Fabriqué",
});

export const sourceLabel = (s) => SOURCE_LABELS[s] || s || "—";

export const SOURCE_OPTIONS = Object.freeze([
  { value: "ACHETE", label: "Acheté (revente)" },
  { value: "FABRIQUE", label: "Fabriqué (production)" },
]);

// ── Connexion status → { label, tone, icon } (FournisseurPharmacieStatus) ─────
// PENDING/ACCEPTED/REJECTED are the base states; REVOKED/EXPIRED are the OD-3
// extensions the connection service emits (revoke → REVOKED; TTL → EXPIRED).
export const CONNEXION_STATUT_META = Object.freeze({
  PENDING: { label: "En attente", tone: "warning", icon: "hourglass" },
  ACCEPTED: { label: "Acceptée", tone: "success", icon: "check-circle" },
  REJECTED: { label: "Refusée", tone: "danger", icon: "x-circle" },
  REVOKED: { label: "Révoquée", tone: "neutral", icon: "ban" },
  EXPIRED: { label: "Expirée", tone: "neutral", icon: "clock" },
});

export const connexionStatutMeta = (s) =>
  CONNEXION_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

/** X §11.1 (XWP-28) — the tab of the pharmacies' requests waiting for this org's answer. */
export const CONNEXION_ONGLET_RECUES = "RECUES";

export const CONNEXION_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: CONNEXION_ONGLET_RECUES, label: "Demandes reçues" },
  { value: "PENDING", label: "En attente" },
  { value: "ACCEPTED", label: "Acceptées" },
  { value: "REJECTED", label: "Refusées" },
  { value: "REVOKED", label: "Révoquées" },
]);

// ── Pricing layer → label (resolvePrice applied_layers) ───────────────────────
export const PRICING_LAYER_LABELS = Object.freeze({
  base: "Prix de cession",
  pharmacy: "Tarif pharmacie",
  bracket: "Palier de quantité",
});

export const pricingLayerLabel = (l) => PRICING_LAYER_LABELS[l] || l || "—";

// ── The paid module code gating per-pharmacy pricing + brackets (FR-02-15) ────
export const TARIFICATION_AVANCEE = "TARIFICATION_AVANCEE";

// ── DTO field accessors (OD-1 Option A — clean names over legacy columns) ─────
// The service exposes coutRevient / prixCession / ppa / tauxTva / margeDistributeur
// / margeImpliquee; these readers also tolerate raw column fallbacks so the UI
// renders correctly whichever shape the backend returns.
const firstDefined = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null && v !== "") return v;
  return null;
};

export const catalogueDto = Object.freeze({
  coutRevient: (e) => firstDefined(e?.coutRevient, e?.prix_achat),
  prixCession: (e) => firstDefined(e?.prixCession, e?.prix_achat_ht),
  ppa: (e) => firstDefined(e?.ppa),
  tauxTva: (e) => firstDefined(e?.tauxTva, e?.taux_tva),
  margeDistributeur: (e) => firstDefined(e?.margeDistributeur),
  margeImpliquee: (e) => firstDefined(e?.margeImpliquee),
});

// ════════════════════════════════════════════════════════════════════════════
// MODULE 03 — COMMERCIAL (Pré-commande → Offre → Bon de Commande)
// ════════════════════════════════════════════════════════════════════════════

// ── Pré-commande status → { label, tone, icon } (StatutPreCommande, §7.1) ─────
// Base values + the E2 on-behalf front-gate states (Appendix P.2.2).
export const PRECOMMANDE_STATUT_META = Object.freeze({
  EN_ATTENTE: { label: "En attente", tone: "warning", icon: "hourglass" },
  ACCEPTEE: { label: "Acceptée", tone: "success", icon: "check-circle" },
  REFUSEE: { label: "Refusée", tone: "danger", icon: "x-circle" },
  ANNULEE: { label: "Annulée", tone: "neutral", icon: "ban" },
  CONVERTIE: { label: "Convertie", tone: "accent", icon: "file-check" },
  EXPIREE: { label: "Expirée", tone: "neutral", icon: "clock" },
  EN_ATTENTE_CONFIRMATION_PHARMACIE: {
    label: "À confirmer (pharmacie)",
    tone: "info",
    icon: "user-check",
  },
  REFUSEE_CONFIRMATION: { label: "Confirmation refusée", tone: "danger", icon: "x-circle" },
});

export const precommandeStatutMeta = (statut) =>
  PRECOMMANDE_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

/** Status filter tabs for the supplier pré-commande inbox (flow order). */
export const PRECOMMANDE_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "EN_ATTENTE", label: "En attente" },
  { value: "ACCEPTEE", label: "Acceptées" },
  { value: "CONVERTIE", label: "Converties" },
  { value: "REFUSEE", label: "Refusées" },
  { value: "EXPIREE", label: "Expirées" },
]);

// ── Pré-commande origine → { label, tone, icon } (OriginePreCommande, E2) ─────
export const PRECOMMANDE_ORIGINE_META = Object.freeze({
  PHARMACIE: { label: "Pharmacie", tone: "neutral", icon: "hospital" },
  DELEGUE: { label: "Délégué", tone: "info", icon: "user-round" },
});

export const precommandeOrigineMeta = (o) =>
  PRECOMMANDE_ORIGINE_META[o] || { label: o || "—", tone: "neutral", icon: "user" };

// ── Offre status → { label, tone, icon } (StatutOffre, §7.2) ──────────────────
export const OFFRE_STATUT_META = Object.freeze({
  EN_ATTENTE: { label: "En attente", tone: "warning", icon: "hourglass" },
  ACCEPTEE: { label: "Acceptée", tone: "success", icon: "check-circle" },
  REFUSEE: { label: "Refusée", tone: "danger", icon: "x-circle" },
  EXPIREE: { label: "Expirée", tone: "neutral", icon: "clock" },
  CONTRE_PROPOSEE: { label: "Contre-proposée", tone: "info", icon: "repeat" },
});

export const offreStatutMeta = (statut) =>
  OFFRE_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

// ── Bon de Commande status → { label, tone, icon } (StatutBonCommande, §7.4) ──
export const BC_STATUT_META = Object.freeze({
  PROPOSE: { label: "Proposé", tone: "warning", icon: "file-clock" },
  CONFIRME: { label: "Confirmé", tone: "success", icon: "check-circle" },
  CLOTURE: { label: "Clôturé", tone: "neutral", icon: "archive" },
  ANNULE: { label: "Annulé", tone: "danger", icon: "ban" },
});

export const bcStatutMeta = (statut) =>
  BC_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

/** Status filter tabs for the BC list (lifecycle order). */
export const BC_STATUT_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "PROPOSE", label: "Proposés" },
  { value: "CONFIRME", label: "Confirmés" },
  { value: "CLOTURE", label: "Clôturés" },
  { value: "ANNULE", label: "Annulés" },
]);

/** BC states a side may still confirm / cancel from (pre-freeze). */
export const BC_CONFIRMABLE_STATES = Object.freeze(["PROPOSE"]);
export const BC_CANCELLABLE_STATES = Object.freeze(["PROPOSE"]);

// ── Promotion type → { label, tone, icon } (TypePromotion, Appendix O) ────────
export const PROMO_TYPE_META = Object.freeze({
  UG: { label: "Unité gratuite", tone: "accent", icon: "gift", short: "UG" },
  REMISE_POURCENT: { label: "Remise %", tone: "info", icon: "percent", short: "%" },
  PALIER: { label: "Palier", tone: "info", icon: "layers", short: "Palier" },
});

export const promoTypeMeta = (t) =>
  PROMO_TYPE_META[t] || { label: t || "—", tone: "neutral", icon: "tag", short: "—" };

export const PROMO_TYPE_OPTIONS = Object.freeze([
  { value: "UG", label: "Unité gratuite (X achetés → Y offerts)" },
  { value: "REMISE_POURCENT", label: "Remise en pourcentage" },
  { value: "PALIER", label: "Palier de quantité (remise par seuil)" },
]);

// ── Promotion status → { label, tone, icon } (StatutPromotion, Appendix O) ────
export const PROMO_STATUT_META = Object.freeze({
  BROUILLON: { label: "Brouillon", tone: "neutral", icon: "file-pen" },
  ACTIVE: { label: "Active", tone: "success", icon: "check-circle" },
  SUSPENDUE: { label: "Suspendue", tone: "warning", icon: "pause" },
  EXPIREE: { label: "Expirée", tone: "neutral", icon: "clock" },
});

export const promoStatutMeta = (s) =>
  PROMO_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

export const PROMO_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "ACTIVE", label: "Actives" },
  { value: "BROUILLON", label: "Brouillons" },
  { value: "SUSPENDUE", label: "Suspendues" },
  { value: "EXPIREE", label: "Expirées" },
]);

// ── Promotion scope type → label (TypePromotionScope, Appendix O) ─────────────
export const PROMO_SCOPE_TYPE_OPTIONS = Object.freeze([
  { value: "PRODUIT", label: "Produit (article du catalogue)" },
  { value: "CLASSIFICATION", label: "Classification (Annexe)" },
  { value: "PHARMACIE", label: "Pharmacie (connexion)" },
  { value: "TIER_CONNEXION", label: "Palier de connexion" },
]);

export const PROMO_SCOPE_TYPE_LABELS = Object.freeze({
  PRODUIT: "Produit",
  CLASSIFICATION: "Classification",
  PHARMACIE: "Pharmacie",
  TIER_CONNEXION: "Palier connexion",
});

export const promoScopeTypeLabel = (t) => PROMO_SCOPE_TYPE_LABELS[t] || t || "—";

export const TIER_CONNEXION_OPTIONS = Object.freeze([
  { value: "STANDARD", label: "Standard" },
  { value: "ARGENT", label: "Argent" },
  { value: "OR", label: "Or" },
]);

/** Negotiation turn cap (config mirror — server is authoritative, GC4). */
export const NEGOCIATION_TURN_CAP = 3;

// ════════════════════════════════════════════════════════════════════════════
// MODULE 04 — WAREHOUSE & LOTS (Entrepôt · Emplacement · Stock-lot · Mouvement)
// ════════════════════════════════════════════════════════════════════════════

/** Paid module code gating the 2nd+ active entrepôt (free tier = one, BR-WH-01). */
export const MULTI_ENTREPOT_PRO = "MULTI_ENTREPOT_PRO";

/**
 * WH-6 — near-expiry window in days (server config mirror, GC4). A lot whose
 * `date_peremption` falls within this many days of today is rendered with the
 * near-expiry warning colour in StockParLot. Distinct from the PERIME status the
 * daily sweep sets on past-expiry lots. Server is authoritative.
 */
export const NEAR_EXPIRY_WINDOW_DAYS = 90;

// ── Stock-lot status → { label, tone, icon } (StatutStockLot, §7.1) ───────────
export const STOCK_LOT_STATUT_META = Object.freeze({
  DISPONIBLE: { label: "Disponible", tone: "success", icon: "check-circle" },
  RESERVE: { label: "Réservé", tone: "info", icon: "lock" },
  PERIME: { label: "Périmé", tone: "danger", icon: "calendar-x" },
  BLOQUE: { label: "Bloqué", tone: "warning", icon: "ban" },
  QUARANTAINE: { label: "Quarantaine", tone: "danger", icon: "shield-alert" },
});

export const stockLotStatutMeta = (statut) =>
  STOCK_LOT_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

/** Status filter tabs for the stock-by-lot grid (availability order). */
export const STOCK_LOT_STATUT_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "DISPONIBLE", label: "Disponibles" },
  { value: "RESERVE", label: "Réservés" },
  { value: "PERIME", label: "Périmés" },
  { value: "BLOQUE", label: "Bloqués" },
  { value: "QUARANTAINE", label: "Quarantaine" },
]);

/** Expiry quick-filter chips for the stock grid (server `expiry` param). */
export const STOCK_EXPIRY_TABS = Object.freeze([
  { value: "", label: "Toutes péremptions" },
  { value: "near", label: "Proche péremption" },
  { value: "expired", label: "Périmés" },
]);

// ── Temperature zone → { label, tone, icon } (TemperatureZone — cold chain) ───
export const TEMPERATURE_ZONE_META = Object.freeze({
  AMBIANT: { label: "Ambiant", tone: "neutral", icon: "thermometer", short: "15–25°C" },
  FROID_POSITIF: { label: "Froid positif", tone: "info", icon: "snowflake", short: "2–8°C" },
  FROID_NEGATIF: { label: "Froid négatif", tone: "info", icon: "thermometer-snowflake", short: "≤ -15°C" },
});

export const temperatureZoneMeta = (z) =>
  TEMPERATURE_ZONE_META[z] || { label: z || "—", tone: "neutral", icon: "thermometer", short: "" };

export const TEMPERATURE_ZONE_OPTIONS = Object.freeze([
  { value: "AMBIANT", label: "Ambiant (15–25°C)" },
  { value: "FROID_POSITIF", label: "Froid positif (2–8°C)" },
  { value: "FROID_NEGATIF", label: "Froid négatif (≤ -15°C)" },
]);

// ── Movement type → { label, tone, icon } (TypeMouvementStock, immutable ledger) ─
export const MOUVEMENT_TYPE_META = Object.freeze({
  ENTREE: { label: "Entrée", tone: "success", icon: "arrow-down-to-line" },
  SORTIE: { label: "Sortie", tone: "warning", icon: "arrow-up-from-line" },
  TRANSFERT: { label: "Transfert", tone: "info", icon: "arrow-left-right" },
  CORRECTION: { label: "Correction", tone: "neutral", icon: "sliders-horizontal" },
  PEREMPTION: { label: "Péremption", tone: "danger", icon: "calendar-x" },
  RESERVATION: { label: "Réservation", tone: "info", icon: "lock" },
  LIBERATION: { label: "Libération", tone: "neutral", icon: "unlock" },
  QUARANTAINE: { label: "Quarantaine", tone: "danger", icon: "shield-alert" },
  LEVEE_QUARANTAINE: { label: "Levée quarantaine", tone: "neutral", icon: "shield-check" },
});

export const mouvementTypeMeta = (t) =>
  MOUVEMENT_TYPE_META[t] || { label: t || "—", tone: "neutral", icon: "circle" };

/** Type filter tabs for the movement ledger (in flow order). */
export const MOUVEMENT_TYPE_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "ENTREE", label: "Entrées" },
  { value: "SORTIE", label: "Sorties" },
  { value: "TRANSFERT", label: "Transferts" },
  { value: "RESERVATION", label: "Réservations" },
  { value: "LIBERATION", label: "Libérations" },
  { value: "CORRECTION", label: "Corrections" },
  { value: "PEREMPTION", label: "Péremptions" },
]);

/**
 * Near-expiry classifier for a lot's péremption date. Returns:
 *   "expired" — past today (danger), "near" — within NEAR_EXPIRY_WINDOW_DAYS
 *   (warning), or null (no special colour). Pure; the server PERIME status is
 *   authoritative for FEFO/availability, this only tints the date cell.
 */
export const expiryStateOf = (datePeremption) => {
  if (!datePeremption) return null;
  const exp = new Date(datePeremption);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((exp.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0) return "expired";
  if (diffDays <= NEAR_EXPIRY_WINDOW_DAYS) return "near";
  return null;
};

// ════════════════════════════════════════════════════════════════════════════
// MODULE 05 — LOGISTICS & DELIVERY (BL transit · Colis/QR · Tournée · Fleet ·
// Cold-chain · Live tracking · Recall pharmacy-alert trace)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Paid module codes (Implementation_Plan §9 / Appendix K). `LOGISTIQUE_PRO`
 * gates the colis/tournée/tracking + driver app; `PARC_VEHICULES` gates fleet
 * management. Basic BL/BR + cold-chain logging stay free core. The free tier
 * hits a 402 { preview:true } envelope and degrades to a preview surface.
 */
export const LOGISTIQUE_PRO = "LOGISTIQUE_PRO";
export const PARC_VEHICULES = "PARC_VEHICULES";

/** BL status filter tabs for the livraison register (document-flow order). */
export const BL_STATUT_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "EN_ATTENTE", label: "En attente" },
  { value: "PREPARE", label: "Préparés" },
  { value: "CHARGE", label: "Chargés" },
  { value: "EN_TRANSIT", label: "En transit" },
  { value: "LIVRE", label: "Livrés" },
  { value: "PARTIELLEMENT_LIVRE", label: "Partiels" },
  { value: "INCIDENT", label: "Incidents" },
  { value: "REFUSE", label: "Refusés" },
]);

// ── Colis status → { label, tone, icon } (StatutColis, §7.1) ──────────────────
export const COLIS_STATUT_META = Object.freeze({
  PREPARE: { label: "Préparé", tone: "neutral", icon: "package" },
  CHARGE: { label: "Chargé", tone: "info", icon: "package-plus" },
  EN_TRANSIT: { label: "En transit", tone: "accent", icon: "truck" },
  LIVRE: { label: "Livré", tone: "success", icon: "check-circle" },
  INCIDENT: { label: "Incident", tone: "danger", icon: "alert-triangle" },
  ANNULE: { label: "Annulé", tone: "neutral", icon: "ban" },
});

export const colisStatutMeta = (statut) =>
  COLIS_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

/**
 * The kanban columns for ColisBoard, in transit order (BR-LOG-01). ANNULE is
 * intentionally excluded from the board (cancelled colis are terminal/hidden).
 */
export const COLIS_BOARD_COLUMNS = Object.freeze([
  "PREPARE",
  "CHARGE",
  "EN_TRANSIT",
  "LIVRE",
  "INCIDENT",
]);

/** Colis states from which the colis may still be assigned (pre-dispatch). */
export const COLIS_ASSIGNABLE_STATES = Object.freeze(["PREPARE", "CHARGE"]);
/** Colis states from which the one-scan dispatch may run (pre-transit). */
export const COLIS_DISPATCHABLE_STATES = Object.freeze(["PREPARE", "CHARGE"]);
/** Colis states from which the colis may be cancelled (pre-dispatch; BR-COLIS-09). */
export const COLIS_CANCELLABLE_STATES = Object.freeze(["PREPARE", "CHARGE"]);

// ── Tournée status → { label, tone, icon } (StatutTournee, §7.3) ──────────────
export const TOURNEE_STATUT_META = Object.freeze({
  PLANIFIEE: { label: "Planifiée", tone: "neutral", icon: "calendar" },
  EN_COURS: { label: "En cours", tone: "accent", icon: "navigation" },
  CLOTUREE: { label: "Clôturée", tone: "success", icon: "check-circle" },
  ANNULEE: { label: "Annulée", tone: "danger", icon: "ban" },
});

export const tourneeStatutMeta = (statut) =>
  TOURNEE_STATUT_META[statut] || { label: statut || "—", tone: "neutral", icon: "circle" };

export const TOURNEE_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "PLANIFIEE", label: "Planifiées" },
  { value: "EN_COURS", label: "En cours" },
  { value: "CLOTUREE", label: "Clôturées" },
  { value: "ANNULEE", label: "Annulées" },
]);

// ── Colis tracking-event type → { label, tone, icon } (TypeEvenementColis) ────
// The immutable EvenementColis trail (live tracking + performance). Ordered as
// the lifecycle reads: CREE → AFFECTE → CHARGE → SCAN_DEPART → EN_TRANSIT →
// CHECKPOINT* → LIVRE → POD_CAPTUREE (+ INCIDENT).
export const EVENEMENT_COLIS_META = Object.freeze({
  CREE: { label: "Colis créé", tone: "neutral", icon: "package-plus" },
  AFFECTE: { label: "Affecté", tone: "info", icon: "user-check" },
  CHARGE: { label: "Chargé", tone: "info", icon: "package-check" },
  SCAN_DEPART: { label: "Scan départ", tone: "accent", icon: "scan-line" },
  EN_TRANSIT: { label: "En transit", tone: "accent", icon: "truck" },
  CHECKPOINT: { label: "Point de passage", tone: "info", icon: "map-pin" },
  INCIDENT: { label: "Incident", tone: "danger", icon: "alert-triangle" },
  LIVRE: { label: "Livré", tone: "success", icon: "check-circle" },
  POD_CAPTUREE: { label: "Preuve de livraison", tone: "success", icon: "file-signature" },
});

export const evenementColisMeta = (type) =>
  EVENEMENT_COLIS_META[type] || { label: type || "—", tone: "neutral", icon: "circle" };

// ── Proof-of-delivery medium → { label, icon } (TypePreuveLivraison / POD) ────
export const POD_TYPE_META = Object.freeze({
  SIGNATURE: { label: "Signature", icon: "file-signature" },
  PHOTO: { label: "Photo", icon: "camera" },
  CODE: { label: "Code", icon: "key-round" },
});

export const podTypeMeta = (t) =>
  POD_TYPE_META[t] || { label: t || "—", icon: "file-check" };

export const POD_TYPE_OPTIONS = Object.freeze([
  { value: "SIGNATURE", label: "Signature du destinataire" },
  { value: "PHOTO", label: "Photo (bon signé / colis remis)" },
  { value: "CODE", label: "Code de confirmation" },
]);

// ── Temperature-reading source → label (SourceReleveTemp, R6) ─────────────────
export const RELEVE_SOURCE_OPTIONS = Object.freeze([
  { value: "MANUEL", label: "Saisie manuelle" },
  { value: "SONDE", label: "Sonde" },
]);

export const RELEVE_SOURCE_LABELS = Object.freeze({
  MANUEL: "Manuel",
  SONDE: "Sonde",
});

export const releveSourceLabel = (s) => RELEVE_SOURCE_LABELS[s] || s || "—";

/**
 * Cold-chain expected bands per TemperatureZone (config mirror, GC4 —
 * COLD_CHAIN_BANDS). The server derives the band from the coldest required zone
 * among a colis's allocated lots and is authoritative for the breach verdict;
 * the client uses this only to render the expected band when the server has not
 * yet stamped borne_min/borne_max on a reading.
 */
export const COLD_CHAIN_BANDS = Object.freeze({
  FROID_POSITIF: { min: 2, max: 8 },
  FROID_NEGATIF: { min: -25, max: -15 },
});

/**
 * Cold-vehicle assignment mode (config mirror, GC4 — COLD_CHAIN_VEHICLE_MODE).
 * Default 'warn' (a fresh tenant may have no fleet modelled). The server is
 * authoritative; the UI uses this to decide whether a cold-chain colis without a
 * cold-capable vehicle is a hard block or a soft warning at assignment time.
 */
export const COLD_CHAIN_VEHICLE_MODE = "warn";

/** Recall pharmacy-alert seam status → { label, tone, icon } (StatutAlertePharmacie). */
export const PHARMACY_ALERT_STATUT_META = Object.freeze({
  NON_APPLICABLE: { label: "Non applicable", tone: "neutral", icon: "minus-circle" },
  EN_ATTENTE_TRACE: { label: "Traçabilité requise", tone: "warning", icon: "search" },
  ALERTE_ENVOYEE: { label: "Alerte envoyée", tone: "success", icon: "bell-ring" },
});

export const pharmacyAlertStatutMeta = (s) =>
  PHARMACY_ALERT_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

// ════════════════════════════════════════════════════════════════════════════
// MODULE 06 — FINANCE & SETTLEMENT (Receivables/AR · Paiement/imputation ·
// Échéancier + deferred-cheque · Avoir (3 mechanisms) · Trésorerie · Credit
// control (411) · Comptabilité PCG / journal / period-close)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Paid module code (Implementation_Plan §9 / Appendix K). `COMPTABILITE_PRO`
 * gates the double-entry book (journal / grand-livre / balance / period-close /
 * FEC). The receivables / payments / échéancier / avoir / trésorerie / credit
 * control / relance are FREE CORE. The free tier hits a 402 { preview:true }
 * envelope on the comptabilité reads and degrades to a preview surface.
 */
export const COMPTABILITE_PRO = "COMPTABILITE_PRO";

// ── Receivable derived StatutReglement → { label, tone, icon } (FIN-3 projection) ─
// NOT a StatutFactureFournisseur value — the receivable service derives it from
// montant_ttc − montant_avoir_total vs montant_paye + the due date.
export const REGLEMENT_STATUT_META = Object.freeze({
  NON_DU: { label: "Non dû", tone: "neutral", icon: "clock" },
  DU: { label: "Dû", tone: "info", icon: "circle-dollar-sign" },
  PARTIELLEMENT_REGLE: { label: "Partiellement réglé", tone: "warning", icon: "circle-dashed" },
  REGLE: { label: "Réglé", tone: "success", icon: "check-circle" },
  EN_RETARD: { label: "En retard", tone: "danger", icon: "alert-triangle" },
});

export const reglementStatutMeta = (s) =>
  REGLEMENT_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

/** Receivable status filter tabs (the AR board), in settlement-flow order. */
export const REGLEMENT_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "VALIDEE", label: "Validées" },
  { value: "PARTIELLEMENT_ACCEPTEE", label: "Partielles" },
  { value: "PAYEE", label: "Payées" },
]);

/** AR aging bucket keys → { label, tone } (the receivable.service aging shape). */
export const AGING_BUCKETS = Object.freeze([
  { key: "non_du", label: "Non échu", tone: "neutral" },
  { key: "b0_30", label: "0–30 j", tone: "info" },
  { key: "b31_60", label: "31–60 j", tone: "warning" },
  { key: "b61_90", label: "61–90 j", tone: "warning" },
  { key: "b90_plus", label: "90 j +", tone: "danger" },
]);

// ── Mode de règlement → { label, icon } (global ModeReglement; GC2) ───────────
export const MODE_REGLEMENT_META = Object.freeze({
  ESPECES: { label: "Espèces", icon: "banknote" },
  CHEQUE: { label: "Chèque", icon: "scroll-text" },
  CARTE: { label: "Carte", icon: "credit-card" },
  VIREMENT: { label: "Virement", icon: "arrow-left-right" },
  AUTRE: { label: "Autre", icon: "circle-ellipsis" },
});

export const modeReglementMeta = (m) =>
  MODE_REGLEMENT_META[m] || { label: m || "—", icon: "circle" };

export const MODE_REGLEMENT_OPTIONS = Object.freeze([
  { value: "ESPECES", label: "Espèces" },
  { value: "VIREMENT", label: "Virement" },
  { value: "CHEQUE", label: "Chèque" },
  { value: "CARTE", label: "Carte" },
  { value: "AUTRE", label: "Autre" },
]);

// ── Paiement statut + sens + type → meta ──────────────────────────────────────
export const PAIEMENT_STATUT_META = Object.freeze({
  CONFIRME: { label: "Confirmé", tone: "success", icon: "check-circle" },
  ANNULE: { label: "Annulé", tone: "neutral", icon: "ban" },
});

export const paiementStatutMeta = (s) =>
  PAIEMENT_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

/** Movement sens → { label, tone, icon } (global SensMouvement / Paiement.sens). */
export const SENS_MOUVEMENT_META = Object.freeze({
  ENTREE: { label: "Entrée", tone: "success", icon: "arrow-down-to-line" },
  SORTIE: { label: "Sortie", tone: "warning", icon: "arrow-up-from-line" },
  // Paiement.sens uses the ENCAISSEMENT/DECAISSEMENT vocabulary on some rows.
  ENCAISSEMENT: { label: "Encaissement", tone: "success", icon: "arrow-down-to-line" },
  DECAISSEMENT: { label: "Décaissement", tone: "warning", icon: "arrow-up-from-line" },
});

export const sensMouvementMeta = (s) =>
  SENS_MOUVEMENT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

export const PAIEMENT_TYPE_LABELS = Object.freeze({
  REGLEMENT: "Règlement",
  ACOMPTE: "Acompte",
  REMBOURSEMENT: "Remboursement",
});

export const paiementTypeLabel = (t) => PAIEMENT_TYPE_LABELS[t] || t || "—";

export const PAIEMENT_SENS_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "ENCAISSEMENT", label: "Encaissements" },
  { value: "DECAISSEMENT", label: "Décaissements" },
]);

// ── Échéance statut + cheque statut → meta ────────────────────────────────────
export const ECHEANCE_STATUT_META = Object.freeze({
  EN_ATTENTE: { label: "En attente", tone: "info", icon: "clock" },
  PARTIEL: { label: "Partiel", tone: "warning", icon: "circle-dashed" },
  REGLE: { label: "Réglée", tone: "success", icon: "check-circle" },
  EN_RETARD: { label: "En retard", tone: "danger", icon: "alert-triangle" },
});

export const echeanceStatutMeta = (s) =>
  ECHEANCE_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

export const ECHEANCE_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "EN_ATTENTE", label: "À venir" },
  { value: "EN_RETARD", label: "En retard" },
  { value: "REGLE", label: "Réglées" },
]);

export const CHEQUE_STATUT_META = Object.freeze({
  A_ENCAISSER: { label: "À encaisser", tone: "info", icon: "hourglass" },
  ENCAISSE: { label: "Encaissé", tone: "success", icon: "check-circle" },
  REJETE: { label: "Rejeté", tone: "danger", icon: "x-circle" },
  ANNULE: { label: "Annulé", tone: "neutral", icon: "ban" },
});

export const chequeStatutMeta = (s) =>
  CHEQUE_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

// ── NoteDeCredit (Avoir) type + statut → meta ─────────────────────────────────
export const AVOIR_TYPE_META = Object.freeze({
  AVOIR_CREDIT: {
    label: "Avoir crédit",
    tone: "info",
    icon: "wallet",
    hint: "Un crédit sur compte, imputable aux factures à venir (solde courant).",
  },
  REMBOURSEMENT: {
    label: "Remboursement",
    tone: "warning",
    icon: "banknote-arrow-down",
    hint: "Un remboursement en numéraire (décaissement de trésorerie).",
  },
  REMPLACEMENT: {
    label: "Remplacement",
    tone: "accent",
    icon: "repeat",
    hint: "Lié à un BL de remplacement (Module 05). Aucun mouvement d'argent.",
  },
});

export const avoirTypeMeta = (t) =>
  AVOIR_TYPE_META[t] || { label: t || "—", tone: "neutral", icon: "file-minus", hint: "" };

export const AVOIR_TYPE_OPTIONS = Object.freeze([
  { value: "AVOIR_CREDIT", label: "Avoir crédit (réduit les factures à venir)" },
  { value: "REMBOURSEMENT", label: "Remboursement (numéraire — décaissement)" },
  { value: "REMPLACEMENT", label: "Remplacement (BL de remplacement — sans argent)" },
]);

export const AVOIR_STATUT_META = Object.freeze({
  EMISE: { label: "Émise", tone: "info", icon: "file-plus" },
  PARTIELLEMENT_IMPUTEE: { label: "Partiellement imputée", tone: "warning", icon: "circle-dashed" },
  IMPUTEE: { label: "Imputée", tone: "success", icon: "check-circle" },
  ANNULEE: { label: "Annulée", tone: "neutral", icon: "ban" },
});

export const avoirStatutMeta = (s) =>
  AVOIR_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

export const AVOIR_TYPE_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "AVOIR_CREDIT", label: "Avoirs crédit" },
  { value: "REMBOURSEMENT", label: "Remboursements" },
  { value: "REMPLACEMENT", label: "Remplacements" },
]);

// ── Trésorerie movement type + compte type → meta ─────────────────────────────
export const TRESORERIE_TYPE_META = Object.freeze({
  ENCAISSEMENT: { label: "Encaissement", tone: "success", icon: "arrow-down-to-line" },
  DECAISSEMENT: { label: "Décaissement", tone: "warning", icon: "arrow-up-from-line" },
  TRANSFERT: { label: "Transfert", tone: "info", icon: "arrow-left-right" },
});

export const tresorerieTypeMeta = (t) =>
  TRESORERIE_TYPE_META[t] || { label: t || "—", tone: "neutral", icon: "circle" };

export const TRESORERIE_TYPE_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "ENCAISSEMENT", label: "Encaissements" },
  { value: "DECAISSEMENT", label: "Décaissements" },
  { value: "TRANSFERT", label: "Transferts" },
]);

export const COMPTE_TRESORERIE_TYPE_OPTIONS = Object.freeze([
  { value: "BANQUE", label: "Banque" },
  { value: "CAISSE", label: "Caisse" },
]);

export const compteTresorerieTypeLabel = (t) =>
  ({ BANQUE: "Banque", CAISSE: "Caisse" })[t] || t || "—";

// ── Comptabilité — Journal type + Periode statut + écriture sens → meta ───────
export const JOURNAL_TYPE_META = Object.freeze({
  VENTES: { label: "Ventes", tone: "success", icon: "trending-up" },
  ACHATS: { label: "Achats", tone: "info", icon: "trending-down" },
  BANQUE: { label: "Banque", tone: "accent", icon: "landmark" },
  OD: { label: "Opérations diverses", tone: "neutral", icon: "sliders-horizontal" },
});

export const journalTypeMeta = (t) =>
  JOURNAL_TYPE_META[t] || { label: t || "—", tone: "neutral", icon: "book" };

export const PERIODE_STATUT_META = Object.freeze({
  OUVERTE: { label: "Ouverte", tone: "info", icon: "lock-open" },
  CLOTUREE: { label: "Clôturée", tone: "success", icon: "lock" },
});

export const periodeStatutMeta = (s) =>
  PERIODE_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

// ── Credit-hold filter tabs (the 411 accounts list) ───────────────────────────
export const CREDIT_HOLD_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "true", label: "En blocage" },
  { value: "false", label: "Actifs" },
]);

// ════════════════════════════════════════════════════════════════════════════
// MODULE 06 EXT — Payables (AP) · Cheques + bordereaux · Bank-rec · Dunning ·
// Statements · G50/fiscal
// ════════════════════════════════════════════════════════════════════════════

// ── Payable (FactureAchatFournisseur) statut → meta ───────────────────────────
export const PAYABLE_STATUT_META = Object.freeze({
  A_PAYER: { label: "À payer", tone: "warning", icon: "clock" },
  PARTIEL: { label: "Partiel", tone: "warning", icon: "circle-dashed" },
  PARTIELLEMENT_PAYEE: { label: "Partiel", tone: "warning", icon: "circle-dashed" },
  PAYEE: { label: "Payée", tone: "success", icon: "check-circle" },
  ANNULEE: { label: "Annulée", tone: "neutral", icon: "ban" },
});

export const payableStatutMeta = (s) =>
  PAYABLE_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

// ── Cheque (deferred) statut → meta (reuses the échéancier cheque vocabulary +
// the received/issued lifecycle states the EXT cheque service emits). ─────────
export const CHEQUE_LC_STATUT_META = Object.freeze({
  A_ENCAISSER: { label: "À encaisser", tone: "info", icon: "hourglass" },
  EN_PORTEFEUILLE: { label: "En portefeuille", tone: "info", icon: "wallet" },
  REMIS: { label: "Remis", tone: "accent", icon: "send" },
  A_LA_BANQUE: { label: "À la banque", tone: "accent", icon: "landmark" },
  ENCAISSE: { label: "Encaissé", tone: "success", icon: "check-circle" },
  IMPAYE: { label: "Impayé", tone: "danger", icon: "x-circle" },
  REJETE: { label: "Rejeté", tone: "danger", icon: "x-circle" },
  ANNULE: { label: "Annulé", tone: "neutral", icon: "ban" },
});

export const chequeLcStatutMeta = (s) =>
  CHEQUE_LC_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "scroll-text" };

export const CHEQUE_SENS_TABS = Object.freeze([
  { value: "", label: "Tous" },
  { value: "RECU", label: "Reçus" },
  { value: "EMIS", label: "Émis" },
]);

export const CHEQUE_SENS_LABELS = Object.freeze({ RECU: "Reçu", EMIS: "Émis", ENTREE: "Reçu", SORTIE: "Émis" });
export const chequeSensLabel = (s) => CHEQUE_SENS_LABELS[s] || s || "—";

// ── Dunning relance niveau → meta + options ───────────────────────────────────
export const RELANCE_NIVEAU_META = Object.freeze({
  RAPPEL: { label: "Rappel", tone: "info", icon: "bell" },
  RELANCE_1: { label: "Relance 1", tone: "warning", icon: "bell-ring" },
  RELANCE_2: { label: "Relance 2", tone: "warning", icon: "bell-ring" },
  MISE_EN_DEMEURE: { label: "Mise en demeure", tone: "danger", icon: "alert-octagon" },
});

export const relanceNiveauMeta = (n) =>
  RELANCE_NIVEAU_META[n] || { label: n || "—", tone: "neutral", icon: "bell" };

export const RELANCE_NIVEAU_OPTIONS = Object.freeze([
  { value: "RAPPEL", label: "Rappel (courtois)" },
  { value: "RELANCE_1", label: "Relance 1" },
  { value: "RELANCE_2", label: "Relance 2" },
  { value: "MISE_EN_DEMEURE", label: "Mise en demeure" },
]);

// ── Bank-rec line match state → meta ──────────────────────────────────────────
export const BANK_LINE_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "false", label: "Non rapprochées" },
  { value: "true", label: "Rapprochées" },
]);

// ════════════════════════════════════════════════════════════════════════════
// MODULE 07 — ANALYTICS & ROTATION (Dashboard · Rotation · Forecast · Compliance ·
// E1 Radar · E2 KPI cockpit · E3 Report builder · E4 Back-testing · E5 What-if ·
// E6 Profitability — single-sided, read-only over the live read-model)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Paid module codes (Implementation_Plan §9.3 / Appendix K). `ANALYTICS_AVANCE`
 * gates the full BI (12-month history · margin · pharmacy behaviour · comparative ·
 * advanced velocity · advanced delivery-performance + the advanced-BI enterprise
 * surfaces E1 radar / E2 cockpit / E3 report builder / E6 profitability + the full
 * compliance dossier). `PREVISION_STOCK` gates the full État de rotation
 * (ledger-accurate rewind + fast/slow/dead + recommendations) · dead-stock ·
 * reorder · demand forecasting + the forecast-derived enterprise surfaces
 * (E4 back-testing / E5 what-if). The free tier hits a 402 { preview:true }
 * envelope on the gated reads and degrades to a real-but-limited PREVIEW
 * (basic 30-day / top-10 dashboard + a partial-free rotation view).
 */
export const ANALYTICS_AVANCE = "ANALYTICS_AVANCE";
export const PREVISION_STOCK = "PREVISION_STOCK";

/** Free-tier caps mirror (server config is authoritative — Appendix K/R). Used
 *  only to label the preview ("aperçu 30 jours / top 10") — never to compute. */
export const ANALYTICS_FREE_WINDOW_DAYS = 30;
export const ANALYTICS_FREE_TOP_N = 10;

/**
 * The metric "basis" label → { label, hint } (BR-ANA-06 — each metric labels the
 * fact grain it is computed from). Displayed as a small chip next to every metric
 * so the user knows whether a number is order-revenue (frozen BC), billed
 * (E-Facture 707/706), or delivered/shipped (BL line). `cost_to_serve` carries the
 * E6 honest-input disclaimer (GC4 proxy vs. tracked).
 */
export const ANALYTICS_BASIS_META = Object.freeze({
  commande: { label: "commandes confirmées", icon: "file-check", hint: "Base : lignes de bons de commande confirmés/clôturés (le revenu agréé, figé)." },
  commande_confirmee: { label: "commandes confirmées", icon: "file-check", hint: "Base : lignes de bons de commande confirmés/clôturés (le revenu agréé, figé)." },
  facture: { label: "facturé", icon: "file-text", hint: "Base : détail des E-Factures (la projection de facturation, ce que la Finance encaisse)." },
  facture_billing: { label: "facturé", icon: "file-text", hint: "Base : détail des E-Factures (la projection de facturation)." },
  livre: { label: "livré", icon: "truck", hint: "Base : lignes de bons de livraison (le grain expédié/livré)." },
  livraison: { label: "livré", icon: "truck", hint: "Base : lignes de bons de livraison (le grain expédié/livré)." },
  ledger: { label: "mouvements de stock", icon: "history", hint: "Base : le journal immuable des mouvements de stock (SORTIE = consommation)." },
  sortie: { label: "sorties de stock", icon: "arrow-up-from-line", hint: "Base : les mouvements SORTIE sur la fenêtre (la vélocité réelle)." },
  cout_lot: { label: "coût lot expédié", icon: "layers", hint: "Base : le coût d'achat du lot expédié (marge réalisée sur ligne livrée)." },
  cout_moyen: { label: "coût moyen produit", icon: "calculator", hint: "Base : le coût d'achat moyen produit (marge prévisionnelle, à défaut du lot)." },
  finance: { label: "données Finance", icon: "landmark", hint: "Base : les seams Finance (créances/DSO réels) lorsque le module Finance est actif." },
  tracked: { label: "coût mesuré", icon: "check-circle", hint: "Coût de service mesuré (coût livraison Module 05 + avoirs + incidents froid)." },
  proxy: { label: "coût estimé", icon: "circle-help", hint: "Coût de service estimé (proxy GC4) — à défaut d'un coût de service mesuré." },
});

export const analyticsBasisMeta = (basis) =>
  ANALYTICS_BASIS_META[basis] || { label: basis || "—", icon: "info", hint: "" };

/** Rotation velocity / coverage band → Badge tone (BR-ANA-07/08; the rotation report). */
export const ROTATION_VELOCITY_META = Object.freeze({
  RAPIDE: { label: "Rapide", tone: "success", icon: "rabbit" },
  NORMAL: { label: "Normale", tone: "info", icon: "gauge" },
  MOYEN: { label: "Moyenne", tone: "info", icon: "gauge" },
  LENT: { label: "Lente", tone: "warning", icon: "snail" },
  MORT: { label: "Stock mort", tone: "danger", icon: "package-x" },
  DORMANT: { label: "Dormant", tone: "danger", icon: "package-x" },
});

export const rotationVelocityMeta = (v) =>
  ROTATION_VELOCITY_META[v] || { label: v || "—", tone: "neutral", icon: "circle" };

/** ABC class → { label, tone } (the rotation/products ABC analysis). */
export const ABC_CLASS_META = Object.freeze({
  A: { label: "Classe A", tone: "success" },
  B: { label: "Classe B", tone: "warning" },
  C: { label: "Classe C", tone: "neutral" },
});

export const abcClassMeta = (c) =>
  ABC_CLASS_META[c] || { label: c || "—", tone: "neutral" };

/**
 * Coverage (days of stock remaining) → Badge tone, mirroring the pharmacy
 * StockDomain band: <15 danger · <45 warning · ≤120 success · else info ·
 * null = dormant. Pure helper (the rotation/forecast tables tint with this).
 */
export const coverageTone = (jours) => {
  if (jours == null) return { tone: "neutral", label: "Dormant" };
  const j = Number(jours);
  let tone = "info";
  if (j < 15) tone = "danger";
  else if (j < 45) tone = "warning";
  else if (j <= 120) tone = "success";
  return { tone, label: `${Math.round(j)} j` };
};

/** N-months-ago rewind selector options for the rotation report (BR-ANA-07). */
export const ROTATION_LOOKBACK_OPTIONS = Object.freeze([
  { value: "1", label: "1 mois" },
  { value: "3", label: "3 mois" },
  { value: "6", label: "6 mois" },
  { value: "12", label: "12 mois" },
]);

/** E1 — Insight status → { label, tone, icon } (InsightStatus inbox lifecycle). */
export const INSIGHT_STATUT_META = Object.freeze({
  OUVERT: { label: "Ouvert", tone: "danger", icon: "circle-alert" },
  OPEN: { label: "Ouvert", tone: "danger", icon: "circle-alert" },
  VU: { label: "Vu", tone: "warning", icon: "eye" },
  ACKNOWLEDGED: { label: "Vu", tone: "warning", icon: "eye" },
  EN_COURS: { label: "En cours", tone: "info", icon: "loader" },
  IN_PROGRESS: { label: "En cours", tone: "info", icon: "loader" },
  RESOLU: { label: "Résolu", tone: "success", icon: "check-circle" },
  RESOLVED: { label: "Résolu", tone: "success", icon: "check-circle" },
  IGNORE: { label: "Ignoré", tone: "neutral", icon: "bell-off" },
  DISMISSED: { label: "Ignoré", tone: "neutral", icon: "bell-off" },
});

export const insightStatutMeta = (s) =>
  INSIGHT_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

/** E1 — Insight inbox status tabs (the board columns, lifecycle order). */
export const INSIGHT_STATUT_TABS = Object.freeze([
  { value: "", label: "Toutes" },
  { value: "OUVERT", label: "Ouvertes" },
  { value: "VU", label: "Vues" },
  { value: "EN_COURS", label: "En cours" },
  { value: "RESOLU", label: "Résolues" },
  { value: "IGNORE", label: "Ignorées" },
]);

/**
 * E1 — the legal next-status transitions from a given insight status
 * (OUVERT→VU→EN_COURS→RESOLU/IGNORE). Drives the per-row action buttons; the
 * server is authoritative for the actual transition.
 */
export const INSIGHT_NEXT_ACTIONS = Object.freeze({
  OUVERT: [
    { statut: "VU", label: "Marquer vu", icon: "eye" },
    { statut: "EN_COURS", label: "Prendre en charge", icon: "play" },
    { statut: "IGNORE", label: "Ignorer", icon: "bell-off" },
  ],
  VU: [
    { statut: "EN_COURS", label: "Prendre en charge", icon: "play" },
    { statut: "RESOLU", label: "Résoudre", icon: "check" },
    { statut: "IGNORE", label: "Ignorer", icon: "bell-off" },
  ],
  EN_COURS: [
    { statut: "RESOLU", label: "Résoudre", icon: "check" },
    { statut: "IGNORE", label: "Ignorer", icon: "bell-off" },
  ],
  RESOLU: [],
  IGNORE: [{ statut: "OUVERT", label: "Rouvrir", icon: "rotate-cw" }],
});

/** E1 — Insight severity → { label, tone, icon }. */
export const INSIGHT_SEVERITE_META = Object.freeze({
  CRITIQUE: { label: "Critique", tone: "danger", icon: "alert-octagon" },
  CRITICAL: { label: "Critique", tone: "danger", icon: "alert-octagon" },
  ELEVEE: { label: "Élevée", tone: "danger", icon: "alert-triangle" },
  HIGH: { label: "Élevée", tone: "danger", icon: "alert-triangle" },
  MOYENNE: { label: "Moyenne", tone: "warning", icon: "alert-circle" },
  MEDIUM: { label: "Moyenne", tone: "warning", icon: "alert-circle" },
  FAIBLE: { label: "Faible", tone: "info", icon: "info" },
  LOW: { label: "Faible", tone: "info", icon: "info" },
});

export const insightSeveriteMeta = (s) =>
  INSIGHT_SEVERITE_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

/** E1 — Insight type/kind → { label, icon } (the radar dimensions, BR-ANA-17). */
export const INSIGHT_TYPE_META = Object.freeze({
  CHURN_RISK: { label: "Risque d'attrition", icon: "user-x" },
  MARGIN_EROSION: { label: "Érosion de marge", icon: "trending-down" },
  DEAD_STOCK: { label: "Stock mort", icon: "package-x" },
  EXPIRY_RISK: { label: "Risque de péremption", icon: "calendar-x" },
  DEMAND_ANOMALY: { label: "Anomalie de demande", icon: "activity" },
  STOCK_OUT_RISK: { label: "Risque de rupture", icon: "package-minus" },
  RECEIVABLES_AGING: { label: "Créances âgées", icon: "circle-dollar-sign" },
  COLD_CHAIN_BREACH: { label: "Rupture chaîne du froid", icon: "thermometer-snowflake" },
});

export const insightTypeMeta = (t) =>
  INSIGHT_TYPE_META[t] || { label: t || "—", icon: "radar" };

export const INSIGHT_TYPE_OPTIONS = Object.freeze([
  { value: "", label: "Tous les types" },
  { value: "CHURN_RISK", label: "Risque d'attrition" },
  { value: "MARGIN_EROSION", label: "Érosion de marge" },
  { value: "DEAD_STOCK", label: "Stock mort" },
  { value: "EXPIRY_RISK", label: "Risque de péremption" },
  { value: "DEMAND_ANOMALY", label: "Anomalie de demande" },
  { value: "STOCK_OUT_RISK", label: "Risque de rupture" },
  { value: "RECEIVABLES_AGING", label: "Créances âgées" },
  { value: "COLD_CHAIN_BREACH", label: "Rupture chaîne du froid" },
]);

/** E2 — KPI cockpit: the KPI catalogue (key → label + unit hint). */
export const KPI_CATALOGUE = Object.freeze([
  { value: "chiffre_affaires", label: "Chiffre d'affaires", unit: "money" },
  { value: "marge", label: "Marge brute", unit: "money" },
  { value: "marge_pct", label: "Taux de marge", unit: "pct" },
  { value: "commandes", label: "Nombre de commandes", unit: "int" },
  { value: "panier_moyen", label: "Panier moyen", unit: "money" },
  { value: "taux_rotation", label: "Taux de rotation", unit: "num" },
  { value: "stock_mort", label: "Valeur stock mort", unit: "money" },
  { value: "dso", label: "DSO (délai d'encaissement)", unit: "int" },
  { value: "taux_service", label: "Taux de service livraison", unit: "pct" },
]);

export const kpiCatalogueMeta = (key) =>
  KPI_CATALOGUE.find((k) => k.value === key) || { value: key, label: key || "—", unit: "num" };

/** E2 — the period granularity of a KPI target. */
export const KPI_PERIOD_OPTIONS = Object.freeze([
  { value: "MENSUEL", label: "Mensuel" },
  { value: "TRIMESTRIEL", label: "Trimestriel" },
  { value: "ANNUEL", label: "Annuel" },
]);

/** E2 — the dimension a KPI target is scoped to (global / per pharmacy/lab/product). */
export const KPI_SCOPE_OPTIONS = Object.freeze([
  { value: "GLOBAL", label: "Global (toute l'activité)" },
  { value: "PHARMACIE", label: "Par pharmacie" },
  { value: "LABORATOIRE", label: "Par laboratoire" },
  { value: "PRODUIT", label: "Par produit" },
]);

/** E3 — the report builder dimension allow-list (mirror; server allow-list is authoritative). */
export const REPORT_DIMENSION_OPTIONS = Object.freeze([
  { value: "pharmacie", label: "Pharmacie" },
  { value: "produit", label: "Produit" },
  { value: "laboratoire", label: "Laboratoire" },
  { value: "classification", label: "Classification (Annexe)" },
  { value: "mois", label: "Mois" },
  { value: "entrepot", label: "Entrepôt" },
]);

/** E3 — the report builder measure allow-list. */
export const REPORT_MEASURE_OPTIONS = Object.freeze([
  { value: "chiffre_affaires", label: "Chiffre d'affaires" },
  { value: "quantite", label: "Quantité" },
  { value: "marge", label: "Marge" },
  { value: "marge_pct", label: "Taux de marge" },
  { value: "commandes", label: "Nombre de commandes" },
  { value: "stock_valeur", label: "Valeur de stock" },
]);

/** E3 — scheduled-report cadence options. */
export const REPORT_CADENCE_OPTIONS = Object.freeze([
  { value: "", label: "Aucune (à la demande)" },
  { value: "QUOTIDIEN", label: "Quotidien" },
  { value: "HEBDOMADAIRE", label: "Hebdomadaire" },
  { value: "MENSUEL", label: "Mensuel" },
]);

export const REPORT_CADENCE_LABELS = Object.freeze({
  QUOTIDIEN: "Quotidien",
  HEBDOMADAIRE: "Hebdomadaire",
  MENSUEL: "Mensuel",
});

export const reportCadenceLabel = (c) => REPORT_CADENCE_LABELS[c] || "À la demande";

/** E4 — forecast-accuracy band from a MAPE value → { label, tone } (lower MAPE = better). */
export const forecastAccuracyBand = (mape) => {
  if (mape == null) return { tone: "neutral", label: "—" };
  const m = Number(mape);
  if (m <= 10) return { tone: "success", label: "Excellente" };
  if (m <= 20) return { tone: "info", label: "Bonne" };
  if (m <= 50) return { tone: "warning", label: "Moyenne" };
  return { tone: "danger", label: "Faible" };
};

/** E5 — what-if scenario lever definitions (the workbench inputs; pure simulation). */
export const WHATIF_LEVERS = Object.freeze([
  { key: "remise_pct", label: "Remise / markdown", unit: "%", min: 0, max: 60, step: 5, default: 0 },
  { key: "reorder_delta_pct", label: "Variation réappro", unit: "%", min: -50, max: 100, step: 10, default: 0 },
  { key: "churn_delta_pct", label: "Variation attrition", unit: "%", min: -30, max: 30, step: 5, default: 0 },
  { key: "demande_delta_pct", label: "Variation demande", unit: "%", min: -50, max: 50, step: 10, default: 0 },
]);

/** E6 — the profitability board dimension → label (the ?by= / Tabs selector). */
export const PROFITABILITY_DIMENSIONS = Object.freeze([
  { value: "pharmacie", label: "Par pharmacie", icon: "hospital" },
  { value: "produit", label: "Par produit", icon: "package" },
  { value: "laboratoire", label: "Par laboratoire", icon: "factory" },
]);

/** E6 — the cost-to-serve basis badge → { label, tone } (tracked vs. proxy disclaimer). */
export const COST_TO_SERVE_BASIS_META = Object.freeze({
  tracked: { label: "Coût mesuré", tone: "success", hint: "Coût de service mesuré (livraison + avoirs + incidents froid)." },
  TRACKED: { label: "Coût mesuré", tone: "success", hint: "Coût de service mesuré (livraison + avoirs + incidents froid)." },
  proxy: { label: "Coût estimé (proxy)", tone: "warning", hint: "Coût de service estimé par proxy (GC4) — à interpréter avec prudence." },
  PROXY: { label: "Coût estimé (proxy)", tone: "warning", hint: "Coût de service estimé par proxy (GC4) — à interpréter avec prudence." },
});

export const costToServeBasisMeta = (b) =>
  COST_TO_SERVE_BASIS_META[b] || { label: b || "—", tone: "neutral", hint: "" };

/** Compliance (7th pillar) — psychotrope register movement type → reuse PSYCHOTROPE_MOUVEMENT_META. */
export const COMPLIANCE_INDICATOR_META = Object.freeze({
  OK: { label: "Conforme", tone: "success", icon: "shield-check" },
  ATTENTION: { label: "À surveiller", tone: "warning", icon: "shield-alert" },
  ANOMALIE: { label: "Anomalie", tone: "danger", icon: "shield-x" },
});

export const complianceIndicatorMeta = (s) =>
  COMPLIANCE_INDICATOR_META[s] || { label: s || "—", tone: "neutral", icon: "shield" };

/**
 * Export formats offered on every analytics surface (§C / AN-7). CSV + PDF + XLSX
 * downloads (authenticated blob) + an HTML print. `format` is the ?format= query
 * value; `ext` the downloaded file extension; the print uses a dedicated path.
 */
export const ANALYTICS_EXPORT_FORMATS = Object.freeze([
  { format: "csv", ext: "csv", label: "Exporter CSV", icon: "download" },
  { format: "xlsx", ext: "xlsx", label: "Exporter XLSX", icon: "table" },
  { format: "pdf", ext: "pdf", label: "Exporter PDF", icon: "file-text" },
]);

// ════════════════════════════════════════════════════════════════════════════
// MODULE 08 — SUBSCRIPTION & MONETIZATION (the supplier billing portal:
// plan/à-la-carte catalogue · lifecycle statut · entitlement origines ·
// founding/grandfathered pricing · dunning · referral credits · TVA mirror)
// ════════════════════════════════════════════════════════════════════════════

/**
 * The Catalogue page URL carrying the module the user wants to activate — THE
 * upgrade seam every 402-preview « Activer » CTA points to (TC-SUB-07). The
 * Catalogue reads `?module=` and highlights/scrolls to the plan(s)/add-on
 * carrying that module. `code` optional (no param → the plain catalogue).
 */
export const upgradeUrlForModule = (code) =>
  `/portail-fournisseur/abonnement/catalogue${code ? `?module=${encodeURIComponent(code)}` : ""}`;

/** TVA-on-subscription display mirror (server config is authoritative — SUB-12). */
export const ABONNEMENT_TVA_PCT = 19;

// ── Subscription statut → { label, tone, icon } (StatutAbonnement, §7.1) ──────
// ESSAI/ACTIVE/PAST_DUE/SUSPENDUE/EXPIREE + the cancel-at-period-end terminal
// (the brief says ANNULEE; the backend plan names RESILIEE — both tolerated).
export const ABONNEMENT_STATUT_META = Object.freeze({
  ESSAI: { label: "Essai", tone: "info", icon: "hourglass" },
  ACTIVE: { label: "Actif", tone: "success", icon: "check-circle" },
  PAST_DUE: { label: "Paiement en retard", tone: "warning", icon: "alert-triangle" },
  SUSPENDUE: { label: "Suspendu", tone: "danger", icon: "pause" },
  EXPIREE: { label: "Expiré", tone: "neutral", icon: "clock" },
  ANNULEE: { label: "Annulé", tone: "neutral", icon: "ban" },
  RESILIEE: { label: "Résilié", tone: "neutral", icon: "ban" },
});

export const abonnementStatutMeta = (s) =>
  ABONNEMENT_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

// ── Billing cycle → label (MENSUEL / ANNUEL; annual = ×10 → « 2 mois offerts ») ─
export const ABONNEMENT_CYCLE_META = Object.freeze({
  MENSUEL: { label: "Mensuel", short: "/ mois" },
  ANNUEL: { label: "Annuel", short: "/ an", hint: "2 mois offerts" },
});

export const abonnementCycleMeta = (c) =>
  ABONNEMENT_CYCLE_META[c] || { label: c || "—", short: "" };

// ── Plan tier → { label, tone, icon, blurb } (GRATUIT|ESSENTIEL|PRO|ENTERPRISE) ─
export const PLAN_META = Object.freeze({
  GRATUIT: {
    label: "Gratuit",
    tone: "neutral",
    icon: "leaf",
    blurb: "Le socle libre — opérez votre activité de base.",
  },
  ESSENTIEL: {
    label: "Essentiel",
    tone: "info",
    icon: "package",
    blurb: "Les modules clés pour structurer la distribution.",
  },
  PRO: {
    label: "Pro",
    tone: "accent",
    icon: "rocket",
    blurb: "Le portail complet — logistique, finance et BI avancés.",
  },
  ENTERPRISE: {
    label: "Enterprise",
    tone: "success",
    icon: "building-2",
    blurb: "Tout PHARMAKON, pour les organisations multi-sites.",
  },
});

export const planMeta = (code) =>
  PLAN_META[code] || { label: code || "—", tone: "neutral", icon: "package", blurb: "" };

// ── Entitlement origine → { label, tone, icon, hint } ─────────────────────────
// The brief's vocabulary (PLAN|ALACARTE|TRIAL|ADMIN_GRANT) + the backend plan's
// discriminator (ESSAI|COMP) — both tolerated (parallel build, reconcile later).
export const ENTITLEMENT_ORIGINE_META = Object.freeze({
  PLAN: {
    label: "Inclus dans le plan",
    tone: "accent",
    icon: "layout-grid",
    hint: "Accordé par votre plan d'abonnement actif.",
  },
  ALACARTE: {
    label: "Acheté séparément",
    tone: "info",
    icon: "puzzle",
    hint: "Acheté séparément — reste actif indépendamment du plan.",
  },
  TRIAL: {
    label: "Essai",
    tone: "warning",
    icon: "hourglass",
    hint: "Accordé pendant la période d'essai.",
  },
  ESSAI: {
    label: "Essai",
    tone: "warning",
    icon: "hourglass",
    hint: "Accordé pendant la période d'essai.",
  },
  ADMIN_GRANT: {
    label: "Accordé par PHARMAKON",
    tone: "success",
    icon: "gift",
    hint: "Un accès offert par l'équipe PHARMAKON.",
  },
  COMP: {
    label: "Accordé par PHARMAKON",
    tone: "success",
    icon: "gift",
    hint: "Un accès offert par l'équipe PHARMAKON.",
  },
});

export const entitlementOrigineMeta = (o) =>
  ENTITLEMENT_ORIGINE_META[o] || { label: o || "—", tone: "neutral", icon: "circle", hint: "" };

/**
 * Dunning level → { label, tone } (the J+1/3/7 retry ladder → grace → suspend;
 * SUB-11 mirror — the server drives the ladder, this only labels the banner).
 */
export const abonnementDunningMeta = (niveau) => {
  const n = Number(niveau ?? 0);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n === 1) return { label: "Relance 1 (J+1)", tone: "warning" };
  if (n === 2) return { label: "Relance 2 (J+3)", tone: "warning" };
  if (n === 3) return { label: "Relance 3 (J+7)", tone: "danger" };
  return { label: `Relance ${n}`, tone: "danger" };
};

// ── Subscription payment kind + statut → meta (the Historique table) ──────────
export const ABONNEMENT_PAIEMENT_KIND_META = Object.freeze({
  SUBSCRIPTION: { label: "Abonnement", icon: "credit-card" },
  ABONNEMENT: { label: "Abonnement", icon: "credit-card" },
  PLAN: { label: "Abonnement", icon: "credit-card" },
  ADDON: { label: "Module à la carte", icon: "puzzle" },
  RENEWAL: { label: "Renouvellement", icon: "rotate-cw" },
  RENOUVELLEMENT: { label: "Renouvellement", icon: "rotate-cw" },
  UPGRADE: { label: "Changement de plan", icon: "arrow-up-circle" },
  CHANGEMENT_PLAN: { label: "Changement de plan", icon: "arrow-up-circle" },
});

export const abonnementPaiementKindMeta = (k) =>
  ABONNEMENT_PAIEMENT_KIND_META[k] || { label: k || "—", icon: "receipt" };

export const ABONNEMENT_PAIEMENT_STATUT_META = Object.freeze({
  PAYE: { label: "Payé", tone: "success", icon: "check-circle" },
  PAID: { label: "Payé", tone: "success", icon: "check-circle" },
  CONFIRME: { label: "Payé", tone: "success", icon: "check-circle" },
  EN_ATTENTE: { label: "En attente", tone: "warning", icon: "hourglass" },
  PENDING: { label: "En attente", tone: "warning", icon: "hourglass" },
  ECHOUE: { label: "Échoué", tone: "danger", icon: "x-circle" },
  FAILED: { label: "Échoué", tone: "danger", icon: "x-circle" },
  REMBOURSE: { label: "Remboursé", tone: "info", icon: "banknote-arrow-down" },
  ANNULE: { label: "Annulé", tone: "neutral", icon: "ban" },
});

export const abonnementPaiementStatutMeta = (s) =>
  ABONNEMENT_PAIEMENT_STATUT_META[s] || { label: s || "—", tone: "neutral", icon: "circle" };

/**
 * Paid supplier-module code → { label, icon } (display mirror of the server
 * SUPPLIER_MODULES catalogue — labels only; the catalogue read stays
 * authoritative for availability + pricing). Unknown codes fall back to the
 * raw code so a catalogue-driven module added server-side still renders.
 */
export const SUPPLIER_MODULE_META = Object.freeze({
  CATALOGUE_FOURNISSEUR: { label: "Catalogue & tarifs", icon: "package" },
  PRECOMMANDES: { label: "Pré-commandes", icon: "shopping-cart" },
  TARIFICATION_AVANCEE: { label: "Tarification avancée", icon: "tag" },
  MULTI_ENTREPOT_PRO: { label: "Multi-entrepôts", icon: "warehouse" },
  LOGISTIQUE_PRO: { label: "Logistique Pro", icon: "truck" },
  PARC_VEHICULES: { label: "Parc & chauffeurs", icon: "car-front" },
  COMPTABILITE_PRO: { label: "Comptabilité Pro", icon: "book-open" },
  ANALYTICS_AVANCE: { label: "Analytique avancée", icon: "bar-chart-3" },
  PREVISION_STOCK: { label: "Prévision de stock", icon: "trending-up" },
  API_INTEGRATION: { label: "API & intégrations", icon: "plug" },
  DELEGUES: { label: "Délégués commerciaux", icon: "user-round" },
});

export const supplierModuleMeta = (code) =>
  SUPPLIER_MODULE_META[code] || { label: code || "—", icon: "puzzle" };

/**
 * The itemized effective-price breakdown rows, in the B0 pipeline order
 * (catalogue → base → promo → crédit → HT → TVA 19% → TTC). Each row knows how
 * to read its amount from the breakdown payload (both key spellings tolerated)
 * and whether it renders as a deduction. Used by the checkout confirm dialog +
 * the Historique receipt — the server's persisted breakdown is authoritative.
 */
export const ABONNEMENT_BREAKDOWN_ROWS = Object.freeze([
  { key: "montant_catalogue", label: "Prix catalogue", get: (b) => b?.montant_catalogue ?? b?.prix_catalogue },
  { key: "montant_base", label: "Base appliquée (fondateur / tarif maintenu)", get: (b) => b?.montant_base ?? b?.prix_base },
  { key: "remise_promo", label: "Remise code promo", deduction: true, get: (b) => b?.remise_promo ?? b?.montant_remise },
  { key: "credit_applique", label: "Crédit parrainage appliqué", deduction: true, get: (b) => b?.credit_applique ?? b?.credit },
  { key: "montant_ht", label: "Montant HT", strong: true, get: (b) => b?.montant_ht },
  { key: "montant_tva", label: `TVA ${ABONNEMENT_TVA_PCT}%`, get: (b) => b?.montant_tva, pct: (b) => b?.tva_pct },
  { key: "montant_ttc", label: "Total TTC", strong: true, total: true, get: (b) => b?.montant_ttc },
]);

// ════════════════════════════════════════════════════════════════════════════
// CSV + PRINT helpers (§C cross-cutting — an Exporter CSV + Imprimer action on
// every finance list/report). The backend has no ?format=csv / print endpoints
// yet, so we render the already-loaded rows client-side: a well-formed CSV
// (BOM + CRLF, fr-friendly), and a print via the browser. Money is kept as the
// raw Decimal string in the CSV (no thousands separator) so a spreadsheet parses
// it; the on-screen tables format on display only.
// ════════════════════════════════════════════════════════════════════════════

/** Quote a CSV field (escape `"` + wrap when it holds a separator/quote/newline). */
const csvField = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Build + download a CSV file from columns + rows. `columns` is
 * [{ key, label, get?(row) }]; `rows` is the data array. Uses `;` as the
 * separator (the fr-DZ spreadsheet default) + a UTF-8 BOM so Excel reads the
 * accents. No-op when there are no rows.
 */
export function downloadCsv(filename, columns, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const header = columns.map((c) => csvField(c.label)).join(";");
  const lines = rows.map((row) =>
    columns
      .map((c) => csvField(typeof c.get === "function" ? c.get(row) : row?.[c.key]))
      .join(";"),
  );
  const content = "﻿" + [header, ...lines].join("\r\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return true;
}

/** Print the current view via the browser print dialog (print-credible CSS handles layout). */
export function printView() {
  if (typeof window !== "undefined" && typeof window.print === "function") window.print();
}
